// 표시 언어 스토어 — lib/portal.ts 와 같은 방식(localStorage + 커스텀 이벤트)으로 호스트 전체의
// 표시 언어를 들고 있는다. React 16.10 이라 useSyncExternalStore 대신 useState + 이벤트로 맞춘다.
//
// 결정 순서 (앞이 이긴다):
//   1. 페이지 로딩 시 URL 파라미터 ?lang=ja  (별칭 ?locale=) — initLocale() 이 한 번 읽어 저장한다
//   2. localStorage 에 남은 마지막 선택
//   3. 기본값 'ko'
//
// 모듈(마이크로 프론트)에는 세 갈래로 알린다 — 모듈이 React 버전과 무관하게 따라올 수 있게:
//   · document.documentElement.lang  (스크린 리더·CSS :lang() 도 이 값을 본다)
//   · window.__HOST_LOCALE__          (마운트 시점에 한 번 읽는 용도)
//   · 'hostLocaleChange' CustomEvent  (detail: { lang }) — 전환을 실시간으로 따라가는 용도
import { useEffect, useState } from 'react';
import { MESSAGES, MessageKey } from './locale.messages';

/** 지원 언어 — 3개 이상이므로 KRDS 기준 드롭다운 유형을 쓴다 */
export type LangId = 'ko' | 'ja' | 'en';

export const LANGS: LangId[] = ['ko', 'ja', 'en'];

/** 원어 표기 — 그 언어를 읽는 사람이 알아볼 수 있게 언제나 자기 언어로 쓴다 (KRDS) */
export const LANG_NATIVE: Record<LangId, string> = {
  ko: '한국어',
  ja: '日本語',
  en: 'English',
};

const STORAGE_KEY = 'hostLang';
/** 언어 전환을 알리는 커스텀 이벤트명 (모듈도 구독한다) */
export const LOCALE_EVENT = 'hostLocaleChange';
/** URL 파라미터 이름 — 앞의 것부터 찾는다 */
const PARAM_NAMES = ['lang', 'locale'];

/**
 * 외부에서 들어온 언어 값을 지원 언어로 맞춘다.
 * 'ja-JP', 'JP', 'ko_KR', 'kr', 'en-US' 처럼 흔히 섞여 들어오는 표기를 모두 받는다.
 */
export function normalizeLang(value: string | null | undefined): LangId | null {
  if (!value) return null;
  const head = value.trim().toLowerCase().split(/[-_]/)[0];
  if (head === 'ko' || head === 'kr' || head === 'kor') return 'ko';
  if (head === 'ja' || head === 'jp' || head === 'jpn') return 'ja';
  if (head === 'en' || head === 'eng') return 'en';
  return null;
}

/* localStorage 가 막힌 환경에서도 이번 세션 동안은 선택이 유지되게 하는 대체 저장소 */
let memory: LangId = 'ko';

/** 저장된 선택을 읽는다 — 없거나 모르는 값이면 한국어 */
function read(): LangId {
  try {
    return normalizeLang(localStorage.getItem(STORAGE_KEY)) ?? 'ko';
  } catch {
    // 사파리 프라이빗 등에서 localStorage 접근 자체가 막힐 수 있다 — 메모리 값으로 산다
    return memory;
  }
}

/** 문서·전역에 현재 언어를 반영 — 모듈이 마운트 시점에 읽을 수 있게 */
function apply(lang: LangId): void {
  document.documentElement.lang = lang;
  (window as any).__HOST_LOCALE__ = lang;
}

/** 현재 언어 (훅 밖에서 읽을 때) */
export function getLang(): LangId {
  return read();
}

/** 언어 전환 — 저장하고 문서에 반영한 뒤 구독자에게 알린다 */
export function setLang(lang: LangId): void {
  if (read() === lang) return;
  memory = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // 저장에 실패해도 memory 로 이번 세션은 바뀐 언어를 보여준다
  }
  apply(lang);
  window.dispatchEvent(new CustomEvent(LOCALE_EVENT, { detail: { lang } }));
}

/**
 * 첫 렌더 전에 한 번 호출 — URL 파라미터를 읽어 저장하고 문서에 반영한다.
 *
 * 파라미터는 읽은 뒤 주소창에서 지운다(다른 쿼리·해시는 남긴다). 남겨 두면 사용자가
 * 드롭다운으로 바꾼 뒤 새로고침했을 때 파라미터가 다시 이겨 선택이 되돌아가기 때문이다.
 * 알 수 없는 값(?lang=zz)은 무시하고 저장된 선택을 그대로 쓴다 — 이때도 파라미터는 지운다.
 */
export function initLocale(): LangId {
  const url = new URL(window.location.href);
  let fromParam: LangId | null = null;
  let touched = false;
  for (const name of PARAM_NAMES) {
    if (!url.searchParams.has(name)) continue;
    fromParam = fromParam ?? normalizeLang(url.searchParams.get(name));
    url.searchParams.delete(name);
    touched = true;
  }

  if (fromParam) {
    memory = fromParam;
    try {
      localStorage.setItem(STORAGE_KEY, fromParam);
    } catch {
      // memory 로 대신한다
    }
  }
  if (touched) {
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }

  const lang = read();
  apply(lang);
  return lang;
}

/** 현재 언어를 상태로 구독 — 어느 컴포넌트에서 바꾸든 함께 갱신된다 */
export function useLang(): LangId {
  const [lang, setLangState] = useState<LangId>(read);

  useEffect(() => {
    const sync = () => setLangState(read());
    window.addEventListener(LOCALE_EVENT, sync);
    // 다른 탭에서 바꿨을 때도 따라간다 (storage 이벤트는 같은 탭에서는 안 온다)
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      apply(read());
      sync();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(LOCALE_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return lang;
}

/** 번역 함수 — 해당 언어에 없는 키는 한국어로, 그래도 없으면 키 자체를 보여준다 */
export function translate(lang: LangId, key: MessageKey, vars?: Record<string, string | number>): string {
  let text: string = MESSAGES[lang][key] ?? MESSAGES.ko[key] ?? key;
  if (vars) {
    Object.keys(vars).forEach(k => {
      text = text.split(`{${k}}`).join(String(vars[k]));
    });
  }
  return text;
}

/** 컴포넌트용 — const { lang, t } = useT(); t('nav.home') */
export function useT() {
  const lang = useLang();
  const t = (key: MessageKey, vars?: Record<string, string | number>) => translate(lang, key, vars);
  return { lang, t };
}
